import { useState, useRef, useEffect } from "react";
import type {
  SubtaskId,
  Subtask,
  Task,
  TaskId,
} from "../../../../shared/types";
import { PRIORITY_LABELS } from "../../../../shared/types";
import { getToday, addDays, formatDateShort } from "../../../../shared/types/date-helpers";
import { DatePicker } from "../../../../ui/atoms/DatePicker";
import { useAutoResizeInput } from "../../../../hooks/useAutoResizeInput";
import { ImageGallery } from "../../../images/components/ImageGallery";
import { useCategories, getCategoryBadgeColor } from "../../../../shared/stores/category-store";
import { useDebouncedTaskUpdate } from "./useDebouncedTaskUpdate";

interface TaskItemDetailsProps {
  readonly task: Task;
  readonly onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate" | "subtasks" | "category">
    >,
  ) => void;
  readonly onAddSubtask: (taskId: TaskId, title: string) => void;
  readonly onToggleSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onDeleteSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
}

const DATE_OPTIONS = [
  { label: "Auj.", value: getToday() },
  { label: "Dem.", value: addDays(getToday(), 1) },
  { label: "+1 sem", value: addDays(getToday(), 7) },
] as const;

// Icône calendrier SVG
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

export function TaskItemDetails({
  task,
  onUpdate,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
}: TaskItemDetailsProps) {
  const { categories, labels, dotColors } = useCategories();
  const [localNotes, setLocalNotes] = useState(task.notes ?? "");
  const [newTag, setNewTag] = useState("");
  const [newSubtask, setNewSubtask] = useState("");
  const tagInput = useAutoResizeInput(newTag, 80);
  const subtaskInput = useAutoResizeInput(newSubtask, 180);

  // Debouncing pour éviter les requêtes multiples
  const { update: debouncedUpdate, updateImmediate: immediateUpdate } = useDebouncedTaskUpdate(task.id, onUpdate);

  // Date picker popover
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);

  // Édition inline des sous-tâches
  const [editingSubtaskId, setEditingSubtaskId] = useState<SubtaskId | null>(null);
  const [editSubtaskValue, setEditSubtaskValue] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  // Focus l'input quand l'édition commence
  useEffect(() => {
    if (editingSubtaskId !== null) {
      editInputRef.current?.focus();
    }
  }, [editingSubtaskId]);

  // Flush les updates debouncées quand on quitte le composant
  useEffect(() => {
    return () => {
      // Les updates sont flushées automatiquement par le hook
    };
  }, []);

  const handleAddTag = () => {
    const trimmed = newTag.trim().replace(/^#+/, "");
    if (trimmed && !task.tags.includes(trimmed)) {
      immediateUpdate({ tags: [...task.tags, trimmed] });
      setNewTag("");
    }
  };

  // Commencer l'édition d'une sous-tâche
  const startEditSubtask = (sub: { id: SubtaskId; title: string }) => {
    setEditingSubtaskId(sub.id);
    setEditSubtaskValue(sub.title);
  };

  // Sauvegarder l'édition d'une sous-tâche
  const saveEditSubtask = () => {
    if (editingSubtaskId === null) return;
    const trimmed = editSubtaskValue.trim();
    if (trimmed) {
      const updatedSubtasks = task.subtasks.map((sub) =>
        sub.id === editingSubtaskId
          ? { id: sub.id, title: trimmed, done: sub.done } as Subtask
          : sub
      );
      immediateUpdate({ subtasks: updatedSubtasks });
    }
    setEditingSubtaskId(null);
    setEditSubtaskValue("");
  };

  // Annuler l'édition
  const cancelEditSubtask = () => {
    setEditingSubtaskId(null);
    setEditSubtaskValue("");
  };

  return (
    <>
    <div className="px-4 py-3 bg-surface-alt/30 border-t border-border/40 flex flex-col gap-3">
      {/* Métadonnées : Priorité + Échéance */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 flex-wrap">
        {/* Priorité */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-ink-faint uppercase tracking-wide shrink-0">Priorité</span>
          <div className="flex items-center gap-1">
            {(["urgent", "normal", "low"] as const).map((p) => {
              const dotColor = p === "urgent" ? "bg-danger" : p === "low" ? "bg-ink-faint/30" : "";
              const activeStyle = p === "urgent"
                ? "bg-danger text-white"
                : p === "low"
                  ? "bg-surface-alt text-ink border border-border"
                  : "bg-accent text-white";
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => immediateUpdate({ priority: p })}
                  className={`flex items-center gap-1 px-2 md:px-2.5 py-0.5 md:py-1 text-[10px] md:text-[11px] font-medium rounded-full transition-colors ${
                    task.priority === p
                      ? activeStyle
                      : "bg-surface text-ink-muted hover:text-ink border border-border/60"
                  }`}
                >
                  {dotColor && <span className={`w-1.5 h-1.5 rounded-full ${task.priority === p ? "bg-white/70" : dotColor}`} />}
                  {PRIORITY_LABELS[p].toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Échéance */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-ink-faint uppercase tracking-wide shrink-0">Échéance</span>
          <div className="flex items-center gap-1">
          {/* Bouton calendrier pour date custom */}
          <button
            ref={calendarButtonRef}
            type="button"
            onClick={() => setIsDatePickerOpen(true)}
            className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full transition-colors ${
              task.dueDate && !DATE_OPTIONS.some(opt => opt.value === task.dueDate)
                ? "bg-accent text-white"
                : "bg-surface text-ink-muted hover:text-ink border border-border/60"
            }`}
            title="Choisir une date"
          >
            <CalendarIcon />
          </button>
          {task.dueDate && !DATE_OPTIONS.some(opt => opt.value === task.dueDate) && (
            <span className="px-2.5 py-1 text-[11px] font-medium rounded-full bg-accent/10 text-accent">
              {formatDateShort(task.dueDate)}
            </span>
          )}
          {DATE_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => immediateUpdate({ dueDate: opt.value })}
              className={`px-2 md:px-2.5 py-0.5 md:py-1 text-[10px] md:text-[11px] font-medium rounded-full transition-colors ${
                task.dueDate === opt.value
                  ? "bg-accent text-white"
                  : "bg-surface text-ink-muted hover:text-ink border border-border/60"
              }`}
            >
              {opt.label}
            </button>
          ))}
          </div>
        </div>

        {/* Catégorie */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-ink-faint uppercase tracking-wide shrink-0">Catégorie</span>
          <div className="flex items-center gap-1">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => immediateUpdate({ category: cat })}
                className={`flex items-center gap-1 px-2 md:px-2.5 py-0.5 md:py-1 text-[10px] md:text-[11px] font-medium rounded-full transition-colors ${
                  task.category === cat
                    ? getCategoryBadgeColor(cat)
                    : "bg-surface text-ink-muted hover:text-ink border border-border/60"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dotColors[cat] ?? "bg-gray-400"}`} />
                {labels[cat]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tags inline */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {task.tags.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => immediateUpdate({ tags: task.tags.filter((t) => t !== tag) })}
            className="px-2 py-0.5 text-[10px] bg-accent/10 text-accent hover:bg-accent/20 rounded-full flex items-center gap-1"
          >
            {tag}
            <span className="opacity-60 text-xs">×</span>
          </button>
        ))}
        <div className="flex gap-1">
          <input
            // eslint-disable-next-line react-hooks/refs
            ref={tagInput.ref}
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddTag();
            }}
            placeholder="+ tag"
            className="px-3 py-1 text-xs bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60 transition-all"
            // eslint-disable-next-line react-hooks/refs
            style={tagInput.style}
          />
          <button
            type="button"
            onClick={handleAddTag}
            disabled={!newTag.trim()}
            className="shrink-0 w-8 h-8 flex items-center justify-center bg-accent text-white rounded-full hover:bg-accent/90 disabled:bg-surface-alt disabled:text-ink-muted disabled:cursor-not-allowed transition-colors"
          >
            +
          </button>
        </div>
      </div>

      {/* Sous-tâches */}
      {task.subtasks.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] text-ink-faint uppercase tracking-wide">Sous-tâches</div>
          {task.subtasks.map((sub) => (
            <div key={sub.id} className="flex items-center gap-2 group">
              <button
                type="button"
                onClick={() => onToggleSubtask(task.id, sub.id)}
                className={`shrink-0 w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${
                  sub.done
                    ? "bg-accent border-accent text-white"
                    : "border-border hover:border-accent/50 bg-surface"
                }`}
              >
                {sub.done && (
                  <svg viewBox="0 0 24 24" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={3}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              {editingSubtaskId === sub.id ? (
                <input
                  ref={editInputRef}
                  type="text"
                  value={editSubtaskValue}
                  onChange={(e) => setEditSubtaskValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEditSubtask();
                    if (e.key === "Escape") cancelEditSubtask();
                  }}
                  onBlur={saveEditSubtask}
                  className="flex-1 min-h-6 px-2 text-xs bg-transparent border-b-2 border-accent
                    focus:outline-none"
                />
              ) : (
                <span
                  onDoubleClick={() => startEditSubtask(sub)}
                  className={`text-xs flex-1 cursor-text ${sub.done ? "line-through text-ink-faint" : "text-ink"}`}
                >
                  {sub.title}
                </span>
              )}
              <button
                type="button"
                onClick={() => onDeleteSubtask(task.id, sub.id)}
                className="opacity-0 group-hover:opacity-100 text-ink-faint hover:text-danger text-xs px-1 transition-opacity"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Ajouter sous-tâche */}
      <div className="flex gap-1.5 items-center">
        <input
          // eslint-disable-next-line react-hooks/refs
          ref={subtaskInput.ref}
          type="text"
          value={newSubtask}
          onChange={(e) => setNewSubtask(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && newSubtask.trim()) {
              onAddSubtask(task.id, newSubtask.trim());
              setNewSubtask("");
            }
          }}
          placeholder="Ajouter une sous-tâche..."
          className="px-3 py-2 text-xs bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60 transition-all"
          // eslint-disable-next-line react-hooks/refs
          style={subtaskInput.style}
        />
        <button
          type="button"
          onClick={() => {
            if (newSubtask.trim()) {
              onAddSubtask(task.id, newSubtask.trim());
              setNewSubtask("");
            }
          }}
          disabled={!newSubtask.trim()}
          className="shrink-0 w-8 h-8 flex items-center justify-center bg-accent text-white rounded-full hover:bg-accent/90 disabled:bg-surface-alt disabled:text-ink-muted disabled:cursor-not-allowed transition-colors"
        >
          +
        </button>
      </div>

      {/* Images */}
      <ImageGallery entityType="task" entityId={task.id} />

      {/* Notes */}
      <div>
        <textarea
          value={localNotes}
          onChange={(e) => { setLocalNotes(e.target.value); debouncedUpdate({ notes: e.target.value }); }}
          placeholder="Ajouter des notes..."
          rows={3}
          className="w-full px-3 py-2 text-xs bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60 resize-none placeholder:text-ink-faint/60"
        />
      </div>
    </div>

    {/* DatePicker Popover */}
    {isDatePickerOpen && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/20" onClick={() => setIsDatePickerOpen(false)} />
        <div className="relative z-10" onClick={(e) => e.stopPropagation()}>
          <DatePicker
            value={task.dueDate}
            onChange={(date: string | null) => immediateUpdate({ dueDate: date ?? undefined })}
            onClose={() => setIsDatePickerOpen(false)}
          />
        </div>
      </div>
    )}
  </>
  );
}
